import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddPointFlow from "../AddPoint/AddPointFlow";
import ViewStatementModal from "../ViewStatement/ViewStatementModal";
import WithdrawPanel from "./WithdrawPanel";
import { getAvailableBalance, getRangeList } from "Store/Reducer/common.slice";
import { getMarginSummary } from "Store/Reducer/margin.slice";
import AccountMarginBar from "../../account/AccountMarginBar";
import useAppLanguage from "hooks/useAppLanguage";

const TAB_IDS = ["overview", "add", "withdraw", "statement"];

export default function WalletHubModal({
  onClose,
  user,
  displayName,
  ledgerLabel,
  ledgerColor,
  marginSummary: marginSummaryProp,
  marginSummaryLoading: marginLoadingProp,
  onAddSuccess,
  onRefreshBalance,
  darkTheme = false,
  initialTab = "overview",
}) {
  const dispatch = useDispatch();
  const { t } = useAppLanguage();
  const { availableBalanceLoading } = useSelector((s) => s.common);
  const { marginSummary: marginFromStore, marginSummaryLoading } = useSelector(
    (s) => s.margin || {}
  );
  const summary = marginSummaryProp ?? marginFromStore;
  const summaryLoading = marginLoadingProp ?? marginSummaryLoading;
  const [tab, setTab] = useState(
    TAB_IDS.includes(initialTab) ? initialTab : "overview"
  );
  const tabs = [
    { id: "overview", label: t("overview") },
    { id: "add", label: t("addFunds") },
    { id: "withdraw", label: t("withdraw") },
    { id: "statement", label: t("statement") },
  ];

  const themeClass = darkTheme
    ? "platform-menu-modal-overlay--dark"
    : "platform-menu-modal-overlay--light";

  useEffect(() => {
    if (user?.userId) {
      dispatch(getAvailableBalance());
      dispatch(getMarginSummary());
    }
  }, [dispatch, user?.userId]);

  const refreshBalance = useCallback(() => {
    if (!user?.userId) return;
    dispatch(getAvailableBalance());
    dispatch(getMarginSummary());
  }, [dispatch, user?.userId]);

  const selectTab = useCallback(
    (id) => {
      setTab(id);
      if (id === "add" && user?.userId) dispatch(getRangeList());
    },
    [dispatch, user?.userId]
  );

  const handleWithdrawSuccess = useCallback(() => {
    onRefreshBalance?.();
    dispatch(getMarginSummary());
  }, [onRefreshBalance, dispatch]);

  return (
    <div
      className={`modal-overlay wallet-hub-overlay wallet-hub-overlay--fullpage platform-menu-modal-overlay ${themeClass}`}
      onClick={onClose}
    >
      <div
        className="modal-box wallet-hub-sheet wallet-hub-sheet--fullpage"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wallet-hub-title"
      >
        <div className="modal-header wallet-hub-header">
          <div className="wallet-hub-header__titles">
            <h2 id="wallet-hub-title">{t("account")}</h2>
            <p className="wallet-hub-sub">
              {t("walletSubtitle")}
            </p>
          </div>
          <button type="button" className="modal-close wallet-hub-close" onClick={onClose} aria-label={t("close")}>
            ✕
          </button>
        </div>

        <div className="wallet-hub-balance-strip wallet-hub-balance-strip--margin">
          <AccountMarginBar
            summary={summary}
            loading={summaryLoading || availableBalanceLoading}
            onRefresh={refreshBalance}
            showWithdrawable
          />
          {!summary && (
            <div className="wallet-hub-balance-strip__main" style={{ marginTop: 8 }}>
              <span className="wallet-hub-balance-strip__label">{t("availableBalance")}</span>
              <span
                className="wallet-hub-balance-strip__value"
                style={{ color: ledgerColor || "var(--green)" }}
                title={ledgerLabel || undefined}
              >
                {availableBalanceLoading && !ledgerLabel ? (
                  <span className="wallet-pill__shimmer">···</span>
                ) : (
                  ledgerLabel ?? "—"
                )}
              </span>
            </div>
          )}
        </div>

        <nav className="wallet-hub-tabs" role="tablist">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              className={tab === item.id ? "active" : ""}
              onClick={() => selectTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div
          className={`wallet-hub-body${
            tab === "statement" || tab === "withdraw" || tab === "add"
              ? " wallet-hub-body--flow"
              : ""
          }${tab === "statement" ? " wallet-hub-body--statement" : ""}`}
        >
          {tab === "overview" && (
            <div className="wallet-hub-overview">
              <p className="wallet-hub-overview__text">
                {t("chooseQuickAction")}
              </p>
              <div className="wallet-hub-quick">
                <button
                  type="button"
                  className="wallet-hub-quick__btn wallet-hub-quick__btn--add"
                  onClick={() => selectTab("add")}
                >
                  <span className="wallet-hub-quick__icon" aria-hidden>+</span>
                  <span className="wallet-hub-quick__label">{t("addFunds")}</span>
                  <span className="wallet-hub-quick__hint">{t("depositToLedger")}</span>
                </button>
                <button
                  type="button"
                  className="wallet-hub-quick__btn wallet-hub-quick__btn--withdraw"
                  onClick={() => selectTab("withdraw")}
                >
                  <span className="wallet-hub-quick__icon" aria-hidden>↓</span>
                  <span className="wallet-hub-quick__label">{t("withdraw")}</span>
                  <span className="wallet-hub-quick__hint">{t("requestPayout")}</span>
                </button>
                <button
                  type="button"
                  className="wallet-hub-quick__btn wallet-hub-quick__btn--statement"
                  onClick={() => selectTab("statement")}
                >
                  <span className="wallet-hub-quick__icon wallet-hub-quick__icon--emoji" aria-hidden>📋</span>
                  <span className="wallet-hub-quick__label">{t("statement")}</span>
                  <span className="wallet-hub-quick__hint">{t("viewHistory")}</span>
                </button>
              </div>
            </div>
          )}

          {tab === "add" && (
            <div className="wallet-hub-panel wallet-hub-panel--add">
              <AddPointFlow
                embedded
                user={user}
                onClose={onClose}
                onSuccess={() => {
                  onAddSuccess?.();
                  dispatch(getAvailableBalance());
                  dispatch(getMarginSummary());
                }}
              />
            </div>
          )}

          {tab === "withdraw" && user?.userId && (
            <div className="wallet-hub-panel wallet-hub-panel--withdraw">
              <WithdrawPanel userId={user.userId} onWithdrawSuccess={handleWithdrawSuccess} darkTheme={darkTheme} />
            </div>
          )}

          {tab === "withdraw" && !user?.userId && (
            <p className="wallet-hub-muted">{t("signInWithdraw")}</p>
          )}

          {tab === "statement" && user?.userId && (
            <div className="wallet-hub-panel wallet-hub-panel--statement">
              <ViewStatementModal displayName={displayName} onClose={onClose} embedded />
            </div>
          )}

          {tab === "statement" && !user?.userId && (
            <p className="wallet-hub-muted">{t("signInStatement")}</p>
          )}
        </div>
      </div>
    </div>
  );
}
